import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import SpiderChart from './SpiderChart';
import IndividualScores from './IndividualScores';
import { 
  doc, 
  getDoc, 
  collection, 
  getDocs, 
  query, 
  where
} from 'firebase/firestore';

// Import helper functions
import { 
  WerkvormenModal, useWerkvormenModal
} from '../utils/werkvormenModal';

// Resend Helper Import
import { 
  ResendModal, useResendModal
} from '../utils/resendHelper';

// EXCEL EXPORT HELPER
import {
  downloadExcel,
  validateExcelData,
  getExcelButtonConfig
} from '../utils/excelExportHelper';

// DASHBOARD CALCULATIONS
import {
  calculateTeamScores,
  determineFocusTrede,
  getScoreColor,
  getScoreLabel,
  getPyramidItemClass,
  calculateCompletionStats,
  analyzeTeamPerformance,
  generateRecommendation,
  validateResponseData,
  getProgressBarStyle
} from '../utils/dashboardCalculations';

// TEAM DATA PROCESSOR
import {
  processTeamData,
  TREDES,
  TREDE_NAMES
} from '../utils/teamDataProcessor';

// VIEW HELPERS - UITGEBREID MET LAYOUT CONFIGURATION + ACTION BUTTONS
import {
  getLayoutConfiguration,
  getContentConfiguration,
  getConditionalClasses,
  getViewContent,
  createViewToggleConfig,
  handleViewToggle,
  getToggleButtonClasses,
  preparePyramidData,
  getActionButtonsConfig  // NIEUW - voor conditional export buttons
} from '../utils/dashboardViewHelper';

// Import CSS
import './TeamDashboard.css';

// ✅ FLEXIBELE URL CONFIGURATIE - TOEGEVOEGD
const FUNCTIONS_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://us-central1-relatiescan.cloudfunctions.net'
  : 'http://localhost:5001/relstiescan/us-central1';

const TeamDashboard = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamInfo, setTeamInfo] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [responses, setResponses] = useState([]);
  const [teamScores, setTeamScores] = useState({});
  const [focusTrede, setFocusTrede] = useState(null);
  const [werkvormen, setWerkvormen] = useState([]);
  const [tredeNames, setTredeNames] = useState({});
  const [stats, setStats] = useState({});
  const [viewMode, setViewMode] = useState('pyramid');
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportProgress, setReportProgress] = useState(0);

  // EXCEL EXPORT STATE
  const [isExcelExporting, setIsExcelExporting] = useState(false);
  const [excelSuccess, setExcelSuccess] = useState(false);
  const [excelError, setExcelError] = useState(null);

  // MODAL HOOKS - Clean helper integration
  const werkvormenModal = useWerkvormenModal();
  const resendModal = useResendModal();

  // EMPTY STATE COMPONENT - NIEUW
  const EmptyStateContent = () => (
    <div className="empty-state-content">
      <div className="no-data-icon">📊</div>
      <h2 className="no-data-title">Wachtend op responses</h2>
      <p className="no-data-text">
        Zodra cliënten het assessment invullen verschijnen hier:<br/>
        • Piramide scores per trede<br/>
        • Spinnenweb visualisatie<br/>  
        • Individuele detailscores
      </p>
    </div>
  );

  // Load team data on component mount
  useEffect(() => {
    const loadTeamData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch team info
        const teamDoc = await getDoc(doc(db, 'teams', teamId));
        if (!teamDoc.exists()) {
          throw new Error('Relatie niet gevonden');
        }

        const teamData = teamDoc.data();
        
        // Security check - verify user has access to this team
        if (teamData.createdByEmail !== currentUser.email) {
          throw new Error('Geen toegang tot deze relatie');
        }

        setTeamInfo({ id: teamDoc.id, ...teamData });

        // Fetch invitations
        const invitationsRef = collection(db, 'teams', teamId, 'invites');
        const invitationsSnapshot = await getDocs(invitationsRef);
        const invitationsData = invitationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setInvitations(invitationsData);

        // Fetch responses
        const responsesRef = collection(db, 'responses');
        const responsesQuery = query(responsesRef, where('teamId', '==', teamId));
        const responsesSnapshot = await getDocs(responsesQuery);
        const responsesData = responsesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setResponses(responsesData);

        // Load tredes data
        const tredesData = {};
        const tredeNamesList = ['vertrouwen', 'conflicten', 'betrokkenheid', 'verantwoording', 'resultaten'];
        const tredeNamesMapping = {
          vertrouwen: 'Vertrouwen',
          conflicten: 'Conflicten',
          betrokkenheid: 'Commitment', //Veranderd van Betrokkenheid
          verantwoording: 'Verantwoording',
          resultaten: 'Resultaten'
        };
        setTredeNames(tredeNamesMapping);
        
        for (const tredeName of tredeNamesList) {
          const tredeDoc = await getDoc(doc(db, 'tredes', tredeName));
          if (tredeDoc.exists()) {
            tredesData[tredeName] = tredeDoc.data();
          }
        }

        // Calculate team scores if we have responses
        if (responsesData.length > 0) {
          const scores = calculateTeamScores(responsesData, tredeNamesList);
          const focusTredeResult = determineFocusTrede(scores, tredeNamesList);
          
          setTeamScores(scores);
          setFocusTrede(focusTredeResult);

          // Get werkvormen for focus trede
          if (focusTredeResult && tredesData[focusTredeResult]) {
            setWerkvormen(tredesData[focusTredeResult].werkvormen || []);
          }
        }

        // Calculate stats
        const totalInvited = invitationsData.length;
        const totalCompleted = responsesData.length;
        const totalPending = totalInvited - totalCompleted;
        const responseRate = totalInvited > 0 ? Math.round((totalCompleted / totalInvited) * 100) : 0;
        
        setStats({
          totalInvited,
          totalCompleted,
          totalPending,
          responseRate
        });

      } catch (error) {
        console.error('Error loading team data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (teamId && currentUser) {
      loadTeamData();
    }
  }, [teamId, currentUser]);

  // RESEND SUCCESS HANDLER - Update local state after successful resend
  const handleResendSuccess = (updatedInvitation) => {
    setInvitations(prev => prev.map(inv => 
      inv.id === updatedInvitation.id 
        ? updatedInvitation
        : inv
    ));
  };

  // EXCEL EXPORT HANDLER
  const handleExcelExport = async () => {
    try {
      setIsExcelExporting(true);
      setExcelError(null);
      
      const exportData = {
        teamInfo,
        teamScores,
        tredeNames,
        responses,
        invitations,
        stats,
        focusTrede,
        werkvormen
      };
      
      const result = await downloadExcel(exportData);
      
      if (result.success) {
        setExcelSuccess(true);
        setTimeout(() => setExcelSuccess(false), 5000);
        console.log('✅ Excel export succesvol:', result.filename);
      }
      
    } catch (error) {
      console.error('❌ Excel export fout:', error);
      setExcelError(error.message);
      setTimeout(() => setExcelError(null), 5000);
    } finally {
      setIsExcelExporting(false);
    }
  };

  // ✅ FLEXIBELE RAPPORT GENERATIE - AANGEPAST
 const generatePrintReport = async () => {
  setIsGeneratingReport(true);
  setReportProgress(0);
  
  // Fake progress that increases over time
  const progressInterval = setInterval(() => {
    setReportProgress(prev => {
      if (prev >= 90) return prev; // Stop at 90%, wait for real completion
      return prev + Math.random() * 8; // Random increments
    });
  }, 800);
  
  try {
    console.log('Generating print report for team:', teamId);
    
    const response = await fetch(`${FUNCTIONS_BASE_URL}/generatePrintReport`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ teamId: teamId })
    });
    
    setReportProgress(100); // Jump to 100% when done
    
    if (response.ok) {
      const htmlContent = await response.text();
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
    } else {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Report generation error:', error);
    alert('Fout bij rapport genereren: ' + error.message);
  } finally {
    clearInterval(progressInterval);
    setTimeout(() => {
      setIsGeneratingReport(false);
      setReportProgress(0);
    }, 500); // Show 100% briefly
  }
};

    // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">Team gegevens laden...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="error-container">
        <div className="error-card">
          <div className="error-icon">⚠️</div>
          <h2 className="error-title">Er is een probleem opgetreden</h2>
          <p className="error-message">{error}</p>
          <div className="error-buttons">
            <button onClick={() => navigate('/dashboard')} className="btn-primary">
              Terug naar Dashboard
            </button>
            <button onClick={() => navigate('/')} className="btn-secondary">
              🏠 Terug naar Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  // PROCESS TEAM DATA MET HELPERS - NIEUW
  const teamData = processTeamData(responses);
  const layoutConfig = getLayoutConfiguration(teamData);
  const contentConfig = getContentConfiguration(layoutConfig, viewMode);
  const cssClasses = getConditionalClasses(layoutConfig);
  const viewContent = getViewContent(viewMode, teamData, responses);
  
  // ✅ NIEUW - ACTION BUTTONS CONFIGURATION
  const actionConfig = getActionButtonsConfig(stats);

  // Calculate pyramid items styling for the pyramid view
  const pyramidItems = preparePyramidData(teamData);

  // Main render
  return (
    <div className="team-dashboard" data-testid="team-dashboard">
      
      {/* Header */}
      <div className="dashboard-header" data-testid="dashboard-header">
        <h1 className="dashboard-title" data-testid="dashboard-title">Relatie Dashboard</h1>
        <p className="team-name" data-testid="team-name">{teamInfo?.name}</p>
        <p className="team-creator" data-testid="team-creator">Aangemaakt door: {teamInfo?.createdBy}</p>
      </div>

      <div className="dashboard-content" data-testid="dashboard-content">
        
        {/* Stats Cards - ALTIJD ZICHTBAAR MET RESEND BUTTON */}
        <div className="stats-grid" data-testid="stats-grid">
          <div className="stats-card stats-card--invited" data-testid="stats-invited">
            <div className="stats-number">{stats.totalInvited}</div>
            <div className="stats-label">Uitnodigingen</div>
          </div>
          <div className="stats-card stats-card--completed" data-testid="stats-completed">
            <div className="stats-number">{stats.totalCompleted}</div>
            <div className="stats-label">Voltooid</div>
          </div>
          <div className="stats-card stats-card--pending" data-testid="stats-pending">
            <div className="stats-number">{stats.totalPending}</div>
            <div className="stats-label">Nog te doen</div>
          </div>
          <div className="stats-card stats-card--response-rate" data-testid="stats-response-rate">
            <div className="stats-number">{stats.responseRate}%</div>
            <div className="stats-label">Respons Rate</div>
          </div>
          {/* RESEND BUTTON - ALTIJD ZICHTBAAR */}
          <div 
            className="stats-card stats-card--resend" 
            onClick={resendModal.openModal}
            data-testid="stats-resend"
          >
            <div className="stats-icon">📧</div>
            <div className="stats-label-resend">Verstuur uitnodiging</div>
            <div className="stats-sublabel">opnieuw</div>
          </div>
        </div>

        {/* RESEND SUCCESS MESSAGE */}
        {resendModal.success && (
          <div className="resend-success">
            ✅ Uitnodiging succesvol opnieuw verstuurd
          </div>
        )}

        {/* Progress Section - ALTIJD ZICHTBAAR */}
        <div className="progress-section" data-testid="progress-section">
          <h2 className="progress-title">📊 Voortgang</h2>
          <div className="progress-stats">
            <span>Voltooid: {stats.totalCompleted}</span>
            <span>Totaal: {stats.totalInvited}</span>
          </div>
          <div className="progress-bar-container">
            <div 
              className="progress-bar"
              style={{ width: `${stats.responseRate}%` }}
            ></div>
          </div>
        </div>

        {/* View Toggle - CONDITIONAL VISIBILITY */}
        <div className={cssClasses.toggleSection} data-testid="view-toggle-section">
          <div className="view-toggle-container">
            <span className="view-toggle-label">Bekijk resultaten als:</span>
            {createViewToggleConfig().map(config => (
              <button 
                key={config.type}
                className={getToggleButtonClasses(config.type, viewMode)}
                onClick={() => handleViewToggle(config.type, setViewMode)}
                data-testid={`toggle-${config.type}`}
              >
                {config.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area - PROGRESSIVE DISCLOSURE */}
        {contentConfig.showEmptyState && <EmptyStateContent />}

        {contentConfig.showViewContent && (
          <>
            {viewMode === 'pyramid' && (
              <>
                <div className="pyramid-section" data-testid="pyramid-section">
                  <h2 className="pyramid-title">🔺 Lencioni Piramide Scores</h2>
                  <div className="pyramid-grid">
                    {pyramidItems.map((item) => (
                      <div 
                        key={item.trede}
                        className={`pyramid-item ${item.className} ${item.isFocusTrede ? 'pyramid-item--focus' : ''}`}
                        data-testid={`pyramid-${item.trede}`}
                      >
                        <div className="pyramid-left">
                          <div className={`pyramid-number pyramid-number--${item.status}`}>
                            {item.tredeNumber}
                          </div>
                          <div>
                            <div className={`pyramid-trede-name pyramid-trede-name--${item.status}`}>
                              {item.tredeName}
                            </div>
                            {item.isFocusTrede && (
                              <div className="pyramid-focus-label">Focus punt - Start hier!</div>
                            )}
                          </div>
                        </div>
                        <div className="pyramid-right">
                          <div className={`pyramid-score pyramid-score--${item.status}`}>
                            {item.score.toFixed(1)}
                          </div>
                          <div className={`pyramid-label pyramid-label--${item.status}`}>
                            {item.badgeText}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Focus Recommendation */}
                {teamData.focusTrede && (
                  <div className="recommendation-focus" data-testid="recommendation-focus">
                    <h3 className="recommendation-title recommendation-title--focus">
                      🎯 Prioriteit Aanbeveling
                    </h3>
                    <p className="recommendation-text recommendation-text--focus">
                      {TREDE_NAMES[teamData.focusTrede]} scoort onder de 3.5 punt drempel. Volgens Lencioni's model moeten jullie eerst focussen op het verbeteren van {TREDE_NAMES[teamData.focusTrede]}. 
                      </p>
                  </div>
                )}

                {!teamData.focusTrede && (
                  <div className="recommendation-excellent" data-testid="recommendation-excellent">
                    <h3 className="recommendation-title recommendation-title--excellent">
                      🌟 Uitstekende Performance!
                    </h3>
                    <p className="recommendation-text recommendation-text--excellent">
                      Alle tredes scoren 3.5 of hoger! Jullie hebben een sterke basis.
                    </p>
                  </div>
                )}

                {/* Werkvormen Section - Alleen in pyramid view */}
                {/*werkvormen && werkvormen.length > 0 && (
                  <div className="werkvormen-section" data-testid="werkvormen-section">
                    <h2 className="werkvormen-title">🛠️ Aanbevolen Werkvormen voor {TREDE_NAMES[teamData.focusTrede]}</h2>
                    <div className="werkvormen-grid">
                      {werkvormen.map((werkvorm, index) => (
                        <div key={index} className="werkvorm-card" data-testid={`werkvorm-${index}`}>
                          <h3 
                            className="werkvorm-title"
                            onClick={() => werkvormenModal.openModal(werkvorm)}
                          >
                            {werkvorm.title}
                          </h3>
                          <p className="werkvorm-description">{werkvorm.shortDescription}</p>
                          </div>
                      ))}
                    </div>
                  </div>
                )*/}
              </>
            )}


            {viewMode === 'spider' && (
              <div className="spider-section" data-testid="spider-section">
                <h2 className="section-title">🕸️ Overzicht - Spinnenweb</h2>
                <SpiderChart 
                  teamScores={teamData.scores} 
                  tredeNames={teamData.tredeNames}
                />
              </div>
            )}

            {viewMode === 'individual' && (
              <div className="individual-section" data-testid="individual-section">
                <h2 className="section-title">👥 Individuele Scores</h2>
                <IndividualScores 
                  responses={responses}
                  tredeNames={teamData.tredeNames}
                />
              </div>
            )}
          </>
        )}

        {/* CONDITIONAL ACTION BUTTONS - EXPORT ALLEEN BIJ ALLE VOLTOOID */}
        <div className="action-section" data-testid="action-section">
          <div className="action-buttons">
            
            {/* EXCEL EXPORT - CONDITIONAL VISIBILITY */}
            {/* {actionConfig.showExportButtons && (*/}
            {/*  <button
            //    onClick={handleExcelExport}
            //    disabled={isExcelExporting}
            //    className="action-btn action-btn--secondary"
           //     data-testid="excel-export-button"
           //   >
          //</div>      {isExcelExporting ? 'Excel exporteren...' : 'Download Excel'}
           //   </button>
           // )}

            {/* RAPPORT BUTTON MET PROGRESS - CONDITIONAL VISIBILITY */}
            {actionConfig.showExportButtons && (
              <button 
                onClick={generatePrintReport}
                disabled={isGeneratingReport}
                className={`action-btn action-btn--primary ${isGeneratingReport ? 'action-btn--generating' : ''}`}
                data-testid="generate-report"
              >
                <div>
                  {isGeneratingReport ? 'Rapport genereren...' : 'Maak Rapport'}
                  
                  {isGeneratingReport && (
                    <div className="button-progress-container">
                      <div className="button-progress-bar">
                        <div 
                          className="button-progress-fill"
                          style={{ width: `${reportProgress}%` }}
                        />
                      </div>
                      <div className="button-progress-text">
                        {Math.round(reportProgress)}%
                      </div>
                    </div>
                  )}
                </div>
              </button>
            )}

            {/* ALTIJD ZICHTBARE NAVIGATIE KNOPPEN */}
            <button 
              onClick={() => navigate('/dashboard')}
              className="action-btn action-btn--secondary"
              data-testid="back-to-dashboard"
            >
              ← Terug naar Dashboard
            </button>

            <button 
              onClick={() => navigate('/')}
              className="action-btn action-btn--tertiary"
              data-testid="back-to-homepage"
            >
              🏠 Terug naar Homepage
            </button>

            <button 
              onClick={() => window.location.reload()}
              className="action-btn action-btn--primary"
              data-testid="refresh-dashboard"
            >
              🔄 Ververs Dashboard
            </button>
          </div>
        </div>

        {/* SUCCESS/ERROR MESSAGES FOR EXPORT */}
        {excelSuccess && (
          <div className="export-success" data-testid="excel-success" style={{
            background: '#f0fdf4',
            border: '1px solid #059669', 
            color: '#059669',
            padding: '12px 20px',
            borderRadius: '8px',
            margin: '20px auto',
            textAlign: 'center',
            maxWidth: '500px'
          }}>
            ✅ Excel bestand succesvol gedownload
          </div>
        )}

        {excelError && (
          <div className="export-error" data-testid="export-error" style={{
            background: '#fef2f2',
            border: '1px solid #dc2626',
            color: '#dc2626', 
            padding: '12px 20px',
            borderRadius: '8px',
            margin: '20px auto',
            textAlign: 'center',
            maxWidth: '500px'
          }}>
            ❌ Excel export fout: {excelError}
          </div>
        )}
      </div>

      {/* RESEND MODAL - Helper component */}
      <ResendModal
        isOpen={resendModal.isOpen}
        invitations={invitations}
        responses={responses}
        teamId={teamId}
        teamInfo={teamInfo}
        currentUser={currentUser}
        selectedInvitation={resendModal.selectedInvitation}
        newEmail={resendModal.newEmail}
        isProcessing={resendModal.isProcessing}
        error={resendModal.error}
        success={resendModal.success}
        onClose={resendModal.closeModal}
        onSelectInvitation={resendModal.selectInvitation}
        onEmailChange={resendModal.setNewEmail}
        onProcessResend={resendModal.processResend}
      />

      {/* Werkvormen Modal */}
      <WerkvormenModal 
        werkvorm={werkvormenModal.selectedWerkvorm} 
        isOpen={werkvormenModal.isOpen} 
        onClose={werkvormenModal.closeModal} 
      />
    </div>
  );
}

export default TeamDashboard;